/**
 * The agency-level document areas: Roll-Ups (roll-up dashboards) and Reports
 * (every client's reports and the roll-up reports). Each lists its documents,
 * creates new ones (CreateFlow) and opens one in the builder at
 * #/<area>/<id> (DocumentEditor). Columns follow AgencyAnalytics: name,
 * client (or "N Clients" for a roll-up), type, updated.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { KIND_LABEL } from '../../reports/DocumentItem.tsx';
import {
  type Catalogue,
  type Client,
  type ReportDocument,
  type Template,
} from '../../reports/reports-api.ts';
import { apiCall, orgPath } from '../../api.ts';
import { EmptyFeature } from '../EmptyFeature.tsx';
import { go, hrefFor } from '../routes.ts';
import { CreateFlow } from './CreateFlow.tsx';
import { DocumentEditor } from './DocumentEditor.tsx';

const updated = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const COPY = {
  rollups: {
    title: 'Roll-Ups',
    lead: 'The big-picture view across clients, for your agency and your clients.',
    points: [
      'Aggregate key metrics across clients',
      'Unlock agency-wide insights',
      'Compare clients side by side',
    ] as [string, string, string],
    action: 'Create Dashboard',
  },
  reports: {
    title: 'Reports',
    lead: 'Slide decks and documents for one client or many, in one list.',
    points: [
      'Start from a template, blank, or a copy',
      'Roll up several clients, locations or brands into one report',
      'Present full screen, share a link or email it on a schedule',
    ] as [string, string, string],
    action: 'Create Report',
  },
};

function clientsLabel(document: ReportDocument, clients: Client[]): string {
  if (document.business_unit_id)
    return clients.find((client) => client.id === document.business_unit_id)?.name ?? '';
  const count = document.settings.clients?.length ?? 0;
  return `${count} ${count === 1 ? 'Client' : 'Clients'}`;
}

export function AgencyDocuments(props: {
  area: 'rollups' | 'reports';
  organisationId: string;
  clients: Client[];
  catalogue: Catalogue | null;
  templates: Template[];
  documentId: string | null;
}): ReactNode {
  const { area, organisationId, clients } = props;
  const isReports = area === 'reports';
  const [documents, setDocuments] = useState<ReportDocument[] | null>(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => {
    apiCall<{ documents: ReportDocument[] }>(
      orgPath(
        organisationId,
        `/reports/documents${isReports ? '' : '?scope=ROLLUP&kind=DASHBOARD'}`,
      ),
    )
      .then((data) =>
        setDocuments(
          data.documents.filter((document) => (document.kind !== 'DASHBOARD') === isReports),
        ),
      )
      .catch(() => setDocuments([]));
  }, [organisationId, isReports]);
  useEffect(load, [load, props.documentId]);

  if (props.documentId)
    return (
      <DocumentEditor
        organisationId={organisationId}
        area={area}
        documentId={props.documentId}
        catalogue={props.catalogue}
        clients={clients}
      />
    );
  const copy = COPY[area];
  const create = () => setCreating(true);
  return (
    <section className="ag-page" aria-labelledby="ad-title">
      <header className="ag-head">
        <h1 id="ad-title">{copy.title}</h1>
        <span className="ag-spacer" />
        {!isReports && <a href={hrefFor({ area: 'reports' })}>Roll-up reports</a>}
        {documents && documents.length > 0 && (
          <button type="button" className="btn" onClick={create} disabled={clients.length === 0}>
            {copy.action}
          </button>
        )}
      </header>
      <div className="ag-body">
        {documents === null && <p className="ag-loading">Loading…</p>}
        {documents?.length === 0 && (
          <EmptyFeature
            title={copy.title}
            lead={copy.lead}
            points={copy.points}
            action={clients.length > 0 ? { label: copy.action, onClick: create } : undefined}
          />
        )}
        {documents && documents.length > 0 && (
          <div className="ag-table-card">
            <p className="ag-table-meta">Showing {documents.length} Rows</p>
            <table className="ag-table">
              <thead>
                <tr>
                  <th scope="col">{isReports ? 'Report' : 'Dashboard'}</th>
                  <th scope="col">Client</th>
                  <th scope="col">Type</th>
                  <th scope="col">Updated</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr
                    key={document.id}
                    className="ag-row"
                    onClick={() => go({ area, documentId: document.id })}
                  >
                    <td>
                      <a
                        href={hrefFor({ area, documentId: document.id })}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <strong>{document.name}</strong>
                      </a>
                    </td>
                    <td>{clientsLabel(document, clients)}</td>
                    <td>
                      {document.business_unit_id ? 'Client' : 'Roll-up'}
                      {isReports && (
                        <small className="ag-cell-note">{KIND_LABEL[document.kind]}</small>
                      )}
                    </td>
                    <td>{updated.format(new Date(document.updated_at))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && (
        <CreateFlow
          organisationId={organisationId}
          reports={isReports}
          rollupOnly={!isReports}
          clients={clients}
          templates={props.templates}
          documents={documents ?? []}
          onCancel={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            go({ area, documentId: created.document.id });
          }}
        />
      )}
    </section>
  );
}
